import { createClient, type SupabaseClientOptions } from "@supabase/supabase-js";

export const SUPABASE_URL = "https://tozsbxtxurssvznreikr.supabase.co";
export const SUPABASE_ANON_KEY = "sb_publishable_SKeU2x7nRn0xLWPTTf_ArQ_p6P7RU92";

// ============================================================
// AUTH SESSION STORAGE — DESKTOP USES DURABLE electron-store
// ------------------------------------------------------------
// On the packaged desktop app the UI loads from file://, where
// Chromium's localStorage is the least reliable store — the very
// reason the rest of the app routes its offline cache through
// window.estimatorStore (electron-store, a real JSON file in
// userData). Previously the Supabase session was left in raw
// localStorage, so an unlucky wipe (or just file:// flakiness)
// would silently log the user out. We now back the session with
// the same durable store ON DESKTOP ONLY.
//
// Web and mobile-web have no window.estimator, so they fall through
// to the default localStorage path — their behavior is unchanged.
//
// Read-through migration: the FIRST time the new desktop build runs,
// the existing session still lives in localStorage. getItem copies
// it into electron-store on first read so users (including the admin)
// are NOT forced to re-login after upgrading.
// ============================================================
function isDesktopRuntime(): boolean {
  return (
    typeof window !== "undefined" &&
    (window as any).estimator?.isDesktop === true &&
    !!(window as any).estimatorStore
  );
}

function makeAuthStorage() {
  // Read window.estimatorStore lazily on every call so we never depend
  // on preload timing, and so we degrade gracefully if it disappears.
  const desktopStore = () =>
    isDesktopRuntime() ? (window as any).estimatorStore : null;

  return {
    getItem(key: string): string | null {
      const store = desktopStore();
      if (store) {
        const fromStore = store.get(key);
        if (fromStore !== null && fromStore !== undefined) {
          return typeof fromStore === "string"
            ? fromStore
            : JSON.stringify(fromStore);
        }
        // One-time migration: carry an existing localStorage session into
        // the durable store so upgrading users stay signed in.
        try {
          const legacy =
            typeof window !== "undefined"
              ? window.localStorage.getItem(key)
              : null;
          if (legacy !== null) {
            store.set(key, legacy);
            return legacy;
          }
        } catch {
          /* ignore */
        }
        return null;
      }
      // Web / mobile-web (and desktop fallback if estimatorStore is gone).
      try {
        return typeof window !== "undefined"
          ? window.localStorage.getItem(key)
          : null;
      } catch {
        return null;
      }
    },
    setItem(key: string, value: string): void {
      const store = desktopStore();
      if (store) {
        store.set(key, value);
        return;
      }
      try {
        if (typeof window !== "undefined")
          window.localStorage.setItem(key, value);
      } catch {
        /* ignore */
      }
    },
    removeItem(key: string): void {
      const store = desktopStore();
      if (store) {
        store.remove(key);
        return;
      }
      try {
        if (typeof window !== "undefined")
          window.localStorage.removeItem(key);
      } catch {
        /* ignore */
      }
    },
  };
}

const authOptions: SupabaseClientOptions<"public">["auth"] = {
  persistSession: true,
  autoRefreshToken: true,
  detectSessionInUrl: true,
  storage: makeAuthStorage(),
};

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: authOptions,
});
